import { useEffect, useState } from 'react';
import {
  FaCheckCircle,
  FaCircle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaTimes,
  FaTimesCircle,
} from 'react-icons/fa';
import { getAccountSecurityRequirements } from '../../../utilities/accountSecurityValidation';

function PasswordField({
  id,
  label,
  placeholder,
  value,
  error,
  visible,
  onChange,
  onToggle,
  autoComplete,
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-semibold text-slate-800">
        {label}
      </label>
      <div
        className={[
          'relative overflow-hidden rounded-xl border bg-white transition',
          error ? 'border-red-400 ring-2 ring-red-100' : 'border-slate-300 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-100'
        ].join(' ')}
      >
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full bg-transparent px-4 py-3 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-200"
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          {visible ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
        </button>
      </div>
      {error ? <p className="text-xs font-medium text-red-600">{error}</p> : null}
    </div>
  );
}

function PasswordRequirement({ requirement }) {
  const visualState = {
    met: {
      container: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      icon: <FaCheckCircle className="shrink-0 text-emerald-500" size={14} />,
    },
    unmet: {
      container: 'border-red-200 bg-red-50 text-red-700',
      icon: <FaTimesCircle className="shrink-0 text-red-500" size={14} />,
    },
    pending: {
      container: 'border-slate-200 bg-slate-50 text-slate-500',
      icon: <FaCircle className="shrink-0 text-slate-300" size={10} />,
    },
  };

  const state = visualState[requirement.status] ?? visualState.pending;

  return (
    <div
      className={[
        'flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition',
        state.container,
      ].join(' ')}
    >
      {state.icon}
      <span>{requirement.label}</span>
    </div>
  );
}

function PasswordRequirementsPanel({ requirements }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <div className="grid gap-2 sm:grid-cols-2">
        {requirements.map((requirement) => (
          <PasswordRequirement key={requirement.id} requirement={requirement} />
        ))}
      </div>
    </div>
  );
}

export default function SecurityModal({
  open,
  onClose,
  values,
  errors,
  submitting,
  onChange,
  onSubmit,
}) {
  const [visibility, setVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const passwordRequirements = getAccountSecurityRequirements(values);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !submitting) {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      setVisibility({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
    };
  }, [onClose, open, submitting]);

  if (!open) {
    return null;
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 60 }}>
      <div
        className="absolute inset-0"
        aria-hidden="true"
        onClick={() => {
          if (!submitting) {
            onClose();
          }
        }}
      />

      <div className="modal-panel modal-panel-md relative overflow-hidden rounded-2xl border border-slate-200">
        <div className="bg-red-600 px-6 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white">
                <FaLock size={18} />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white">Seguridad</h2>
                <p className="mt-1 text-sm text-red-50">
                  Actualiza tu contraseña para reforzar la seguridad de tu cuenta.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/20 p-2 text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/25"
              aria-label="Cerrar"
              disabled={submitting}
            >
              <FaTimes size={16} />
            </button>
          </div>
        </div>

        <div className="bg-white px-6 py-6">
          <form className="space-y-5" onSubmit={onSubmit}>
            <PasswordField
              id="currentPassword"
              label="Contraseña actual"
              placeholder="Contraseña actual"
              value={values.currentPassword}
              error={errors.currentPassword}
              visible={visibility.currentPassword}
              onChange={(nextValue) => onChange('currentPassword', nextValue)}
              onToggle={() =>
                setVisibility((prev) => ({
                  ...prev,
                  currentPassword: !prev.currentPassword,
                }))
              }
              autoComplete="current-password"
            />

            <PasswordField
              id="newPassword"
              label="Nueva contraseña"
              placeholder="Nueva contraseña"
              value={values.newPassword}
              error={errors.newPassword}
              visible={visibility.newPassword}
              onChange={(nextValue) => onChange('newPassword', nextValue)}
              onToggle={() =>
                setVisibility((prev) => ({
                  ...prev,
                  newPassword: !prev.newPassword,
                }))
              }
              autoComplete="new-password"
            />

            <PasswordRequirementsPanel requirements={passwordRequirements} />

            <PasswordField
              id="confirmPassword"
              label="Confirmar contraseña"
              placeholder="Confirmar contraseña"
              value={values.confirmPassword}
              error={errors.confirmPassword}
              visible={visibility.confirmPassword}
              onChange={(nextValue) => onChange('confirmPassword', nextValue)}
              onToggle={() =>
                setVisibility((prev) => ({
                  ...prev,
                  confirmPassword: !prev.confirmPassword,
                }))
              }
              autoComplete="new-password"
            />

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={submitting}
              >
                {submitting ? 'Actualizando...' : 'Actualizar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}